import { GameType } from '../types/GameType.ts';

// Type declaration for Google Analytics gtag
declare global {
  interface Window {
    gtag: (
      command: string,
      eventName: string,
      parameters?: Record<string, any>
    ) => void;
  }
}

/**
 * Check if we're in development mode
 * This detects development by checking if we're on localhost or if NODE_ENV is development
 */
function isDevelopmentMode(): boolean {
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    process.env.NODE_ENV === 'development'
  );
}

/**
 * Console log event in development mode
 */
function logToConsole(event: EventType, params: Record<string, any>): void {
  if (isDevelopmentMode()) {
    console.log('📊 Event Logged:', {
      event,
      ...params
    });
  }
}

/**
 * Event Logging System
 * 
 * This module provides a structured event logging system for Google Analytics 4 (GA4).
 * 
 * Key Features:
 * - Centralized event definitions in EventTypes enum
 * - Type-safe event logging with specific functions for each event
 * - Explicit categorization following GA4 best practices
 * - Consistent API with clear function names
 * - Development mode console logging for debugging
 * 
 * Usage Examples:
 * 
 * // Game completion
 * logPuzzleSolve(gameType, gameNumber, pathLength);
 * 
 * // User interactions
 * logClickHowToPlay(gameType, gameNumber);
 * logClickUserStats(gameType, gameNumber);
 * logClickClearBoard(gameType, gameNumber);
 * 
 * // Sharing
 * logShareSuccess(gameType, gameNumber);
 * logShareFailure(gameType, gameNumber, error);
 * 
 * // Navigation
 * logRouteToToday(gameType, source, sourceGameType);
 * logRouteToRandom(gameType, source, sourceGameType);
 * logRouteToGame(destinationGameType, source, sourceGameType);
 * logRouteToPrivacyPolicy();
 * 
 * Adding New Events:
 * 1. Add event to EventTypes enum
 * 2. Create specific function with explicit category from EventCategories enum
 * 3. Choose appropriate category from EventCategories
 * 
 * Example:
 * export const EventTypes = {
 *   CLICK_HINT: 'click_hint',
 * } as const;
 * 
 * export function logClickHint(gameType: GameType, gameNumber: number, hintType: string): void {
 *   logEventWithGameNumber(EventTypes.CLICK_HINT, EventCategories.GAMEPLAY, gameType, gameNumber, { hint_type: hintType });
 * }
 */

/**
 * Event Categories Enum
 * 
 * This enum defines all the event categories used in Google Analytics 4.
 * Having this centralized makes it easier to:
 * - Ensure consistency in category naming
 * - Prevent typos in category names
 * - Make refactoring easier
 * - Provide type safety
 */
export const EventCategories = {
  GAME_COMPLETION: 'game_completion',
  USER_INTERFACE: 'user_interface',
  GAMEPLAY: 'gameplay',
  SOCIAL_SHARING: 'social_sharing',
  NAVIGATION: 'navigation',
  SYSTEM: 'system',
  ERROR: 'error',
} as const;

// Type for event categories
export type EventCategory = typeof EventCategories[keyof typeof EventCategories];

/**
 * Event Types Enum
 * 
 * This enum defines all the events that can be logged in the application.
 * Having this centralized makes it easier to:
 * - Track all events in one place
 * - Ensure consistency in event naming
 * - Prevent typos in event names
 * - Make refactoring easier
 */
export const EventTypes = {
  // Game completion events
  PUZZLE_SOLVE: 'puzzle_solve',

  // Gameplay events
  WORD_ENTERED: 'word_entered',
  INVALID_WORD_ATTEMPT: 'invalid_word_attempt',
  REACHED_NEW_RANK: 'reached_new_rank',
  FOUND_ALL_WORDS: 'found_all_words',

  // User interaction events (with game context)
  CLICK_HOW_TO_PLAY: 'click_how_to_play',
  CLICK_YESTERDAY: 'click_yesterday',
  CLICK_USER_STATS: 'click_user_stats',
  CLICK_CLEAR_BOARD: 'click_clear_board',
  CLICK_PROGRESS_TRACKER: 'click_progress_tracker',
  CLICK_WORDS_FOUND_BANK: 'click_words_found_bank',
  CLICK_RANDOMIZE: 'click_randomize',

  // Sharing events (with game context)
  SHARE_SUCCESS: 'share_success',
  SHARE_FAILURE: 'share_failure',
  SHARE_UNSUPPORTED: 'share_unsupported',

  // Navigation events (without game context)
  ROUTE_TO_TODAY: 'route_to_today',
  ROUTE_TO_RANDOM: 'route_to_random',
  ROUTE_TO_GAME: 'route_to_game',
  ROUTE_TO_PRIVACY_POLICY: 'route_to_privacy_policy',

  // System events
  DICTIONARY_LOADED: 'dictionary_loaded',

  // Error events
  ERROR_JAVASCRIPT: 'error_javascript',
  ERROR_UNHANDLED_REJECTION: 'error_unhandled_rejection',
  ERROR_ASSET_LOAD: 'error_asset_load',
  ERROR_DICTIONARY_LOAD: 'error_dictionary_load',
} as const;

// Type for event names
export type EventType = typeof EventTypes[keyof typeof EventTypes];

/**
 * Base logging function that requires explicit category
 * 
 * @param event - The event type to log
 * @param category - The GA4 event category from EventCategories enum
 * @param additionalParams - Optional additional parameters to include
 */
function logEvent(
  event: EventType,
  category: EventCategory,
  additionalParams?: Record<string, any>
): void {
  const params = {
    event_category: category,
    value: 1,
    ...additionalParams,
  };

  // Log to console in development mode
  logToConsole(event, params);

  // Send to Google Analytics
  if (typeof window.gtag !== 'undefined') {
    window.gtag("event", event, params);
  }
}

/**
 * Base logging function for events with game number context
 * 
 * @param event - The event type to log
 * @param category - The GA4 event category from EventCategories enum
 * @param gameType - The type of game from GameType enum
 * @param gameNumber - The game number for context
 * @param additionalParams - Optional additional parameters to include
 */
function logEventWithGameNumber(
  event: EventType,
  category: EventCategory,
  gameType: GameType,
  gameNumber: number,
  additionalParams?: Record<string, any>
): void {
  const params = {
    event_category: category,
    event_label: gameNumber.toString(),
    value: 1,
    game_type: gameType,
    ...additionalParams,
  };

  // Log to console in development mode
  logToConsole(event, params);

  // Send to Google Analytics
  if (typeof window.gtag !== 'undefined') {
    window.gtag("event", event, params);
  }
}

// Game completion events
/**
 * Log when a user completes a puzzle
 * 
 * @param gameType - The type of game that was solved
 * @param gameNumber - The game number that was solved
 * @param pathLength - The length of the solution path
 */
export function logPuzzleSolve(
  gameType: GameType,
  gameNumber: number,
  pathLength: number
): void {
  logEventWithGameNumber(
    EventTypes.PUZZLE_SOLVE,
    EventCategories.GAME_COMPLETION,
    gameType,
    gameNumber,
    {
      path_length: pathLength
    }
  );
}

// Gameplay events
/**
 * Log when a user enters a word during gameplay
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 * @param word - The word that was entered
 * @param totalWordsEntered - Total number of words entered so far in this game
 */
export function logWordEntered(
  gameType: GameType,
  gameNumber: number,
  word: string,
  totalWordsEntered: number
): void {
  logEventWithGameNumber(
    EventTypes.WORD_ENTERED,
    EventCategories.GAMEPLAY,
    gameType,
    gameNumber,
    {
      word: word.toLowerCase(),
      total_words_entered: totalWordsEntered
    }
  );
}

/**
 * Log when a user attempts an invalid word
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 * @param word - The invalid word that was attempted
 * @param errorMessage - The validation error message
 * @param totalWordsEntered - Total number of words entered so far in this game
 */
export function logInvalidWordAttempt(
  gameType: GameType,
  gameNumber: number,
  word: string,
  errorMessage: string,
  totalWordsEntered: number
): void {
  logEventWithGameNumber(
    EventTypes.INVALID_WORD_ATTEMPT,
    EventCategories.GAMEPLAY,
    gameType,
    gameNumber,
    {
      word: word.toLowerCase(),
      error_message: errorMessage,
      total_words_entered: totalWordsEntered
    }
  );
}

// User interaction events (with game context)
/**
 * Log when user clicks the how to play button
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 */
export function logClickHowToPlay(gameType: GameType, gameNumber: number): void {
  logEventWithGameNumber(EventTypes.CLICK_HOW_TO_PLAY, EventCategories.USER_INTERFACE, gameType, gameNumber);
}

/**
 * Log when user clicks the yesterday button
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 */
export function logClickYesterday(gameType: GameType, gameNumber: number): void {
  logEventWithGameNumber(EventTypes.CLICK_YESTERDAY, EventCategories.USER_INTERFACE, gameType, gameNumber);
}

/**
 * Log when user clicks the user stats button
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 */
export function logClickUserStats(gameType: GameType, gameNumber: number): void {
  logEventWithGameNumber(EventTypes.CLICK_USER_STATS, EventCategories.USER_INTERFACE, gameType, gameNumber);
}

/**
 * Log when user clicks the clear board button
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 */
export function logClickClearBoard(gameType: GameType, gameNumber: number): void {
  logEventWithGameNumber(EventTypes.CLICK_CLEAR_BOARD, EventCategories.GAMEPLAY, gameType, gameNumber);
}

// Sharing events (with game context)
/**
 * Log when user successfully shares their result
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 */
export function logShareSuccess(gameType: GameType, gameNumber: number): void {
  logEventWithGameNumber(EventTypes.SHARE_SUCCESS, EventCategories.SOCIAL_SHARING, gameType, gameNumber);
}

/**
 * Log when sharing fails
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 * @param error - The error that occurred
 */
export function logShareFailure(gameType: GameType, gameNumber: number, error: any): void {
  logEventWithGameNumber(
    EventTypes.SHARE_FAILURE,
    EventCategories.SOCIAL_SHARING,
    gameType,
    gameNumber,
    { error_message: String(error) }
  );
}

/**
 * Log when sharing is not supported on this device
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 */
export function logShareUnsupported(gameType: GameType, gameNumber: number): void {
  logEventWithGameNumber(EventTypes.SHARE_UNSUPPORTED, EventCategories.SOCIAL_SHARING, gameType, gameNumber);
}

// Navigation events (with game context)
/**
 * Log navigation to today's game
 * 
 * @param gameType - The type of game being navigated to
 * @param source - The source of the navigation (optional)
 * @param sourceGameType - The game type context when navigating from a game (optional)
 */
export function logRouteToToday(
  gameType: GameType,
  source?: RouteSourceType,
  sourceGameType?: GameType
): void {
  const additionalParams: Record<string, any> = {};
  if (source) {
    additionalParams.route_source = source;
  }
  if (sourceGameType) {
    additionalParams.source_game_type = sourceGameType;
  }

  logEventWithGameNumber(EventTypes.ROUTE_TO_TODAY, EventCategories.NAVIGATION, gameType, 0, additionalParams);
}

/**
 * Log navigation to a random game
 * 
 * @param gameType - The type of game being navigated to
 * @param source - The source of the navigation (optional)
 * @param sourceGameType - The game type context when navigating from a game (optional)
 */
export function logRouteToRandom(
  gameType: GameType,
  source?: RouteSourceType,
  sourceGameType?: GameType
): void {
  const additionalParams: Record<string, any> = {};
  if (source) {
    additionalParams.route_source = source;
  }
  if (sourceGameType) {
    additionalParams.source_game_type = sourceGameType;
  }

  logEventWithGameNumber(EventTypes.ROUTE_TO_RANDOM, EventCategories.NAVIGATION, gameType, 0, additionalParams);
}

/**
 * Log navigation to a specific game type
 * 
 * @param destinationGameType - The type of game being navigated to
 * @param source - The source of the navigation (optional)
 * @param sourceGameType - The game type context when navigating from a game (optional)
 */
export function logRouteToGame(
  destinationGameType: GameType,
  source?: RouteSourceType,
  sourceGameType?: GameType
): void {
  const additionalParams: Record<string, any> = {};
  if (source) {
    additionalParams.route_source = source;
  }
  if (sourceGameType) {
    additionalParams.source_game_type = sourceGameType;
  }

  logEventWithGameNumber(EventTypes.ROUTE_TO_GAME, EventCategories.NAVIGATION, destinationGameType, 0, additionalParams);
}

/**
 * Log navigation to privacy policy
 */
export function logRouteToPrivacyPolicy(): void {
  logEvent(EventTypes.ROUTE_TO_PRIVACY_POLICY, EventCategories.NAVIGATION);
}

// Additional gameplay events for Subtext
/**
 * Log when user reaches a new rank
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 * @param newRank - The new rank that was reached
 */
export function logReachedNewRank(
  gameType: GameType,
  gameNumber: number,
  newRank: string
): void {
  logEventWithGameNumber(
    EventTypes.REACHED_NEW_RANK,
    EventCategories.GAMEPLAY,
    gameType,
    gameNumber,
    { new_rank: newRank }
  );
}

/**
 * Log when user finds all words in the game
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 * @param totalWordsFound - Total number of words found
 */
export function logFoundAllWords(
  gameType: GameType,
  gameNumber: number,
  totalWordsFound: number
): void {
  logEventWithGameNumber(
    EventTypes.FOUND_ALL_WORDS,
    EventCategories.GAME_COMPLETION,
    gameType,
    gameNumber,
    { total_words_found: totalWordsFound }
  );
}

// Additional user interaction events for Subtext
/**
 * Log when user clicks the progress tracker
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 */
export function logClickProgressTracker(gameType: GameType, gameNumber: number): void {
  logEventWithGameNumber(EventTypes.CLICK_PROGRESS_TRACKER, EventCategories.USER_INTERFACE, gameType, gameNumber);
}

/**
 * Log when user clicks the words found bank
 * 
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 */
export function logClickWordsFoundBank(gameType: GameType, gameNumber: number): void {
  logEventWithGameNumber(EventTypes.CLICK_WORDS_FOUND_BANK, EventCategories.USER_INTERFACE, gameType, gameNumber);
}

/**
 * Log when user clicks the randomize button
 *
 * @param gameType - The type of game
 * @param gameNumber - The current game number
 * @param isRandomized - Whether the letters are being randomized (true) or returned to normal (false)
 */
export function logClickRandomize(gameType: GameType, gameNumber: number, isRandomized: boolean): void {
  logEventWithGameNumber(
    EventTypes.CLICK_RANDOMIZE,
    EventCategories.GAMEPLAY,
    gameType,
    gameNumber,
    { is_randomized: isRandomized }
  );
}

// Error logging functions

/**
 * Log a JavaScript error
 *
 * @param message - The error message
 * @param source - The source file where the error occurred
 * @param lineno - The line number where the error occurred
 * @param colno - The column number where the error occurred
 * @param error - The error object (optional)
 */
export function logJavaScriptError(
  message: string,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error
): void {
  logEvent(
    EventTypes.ERROR_JAVASCRIPT,
    EventCategories.ERROR,
    {
      error_message: message,
      error_source: source || 'unknown',
      error_line: lineno || 0,
      error_column: colno || 0,
      error_stack: error?.stack?.substring(0, 500) || 'no stack', // Truncate stack trace
      error_name: error?.name || 'Error',
      page_url: window.location.pathname,
      user_agent: navigator.userAgent.substring(0, 200), // Truncate user agent
    }
  );
}

/**
 * Log an unhandled promise rejection
 *
 * @param reason - The reason for the rejection
 */
export function logUnhandledRejection(reason: any): void {
  const message = reason instanceof Error ? reason.message : String(reason);
  const stack = reason instanceof Error ? reason.stack?.substring(0, 500) : undefined;

  logEvent(
    EventTypes.ERROR_UNHANDLED_REJECTION,
    EventCategories.ERROR,
    {
      error_message: message,
      error_stack: stack || 'no stack',
      error_name: reason instanceof Error ? reason.name : 'UnhandledRejection',
      page_url: window.location.pathname,
      user_agent: navigator.userAgent.substring(0, 200),
    }
  );
}

/**
 * Log an asset loading error
 *
 * @param assetType - The type of asset that failed to load (e.g., 'image', 'script', 'font')
 * @param assetUrl - The URL of the asset that failed to load
 * @param errorMessage - The error message (optional)
 */
export function logAssetLoadError(
  assetType: string,
  assetUrl: string,
  errorMessage?: string
): void {
  logEvent(
    EventTypes.ERROR_ASSET_LOAD,
    EventCategories.ERROR,
    {
      asset_type: assetType,
      asset_url: assetUrl,
      error_message: errorMessage || 'Failed to load asset',
      page_url: window.location.pathname,
      user_agent: navigator.userAgent.substring(0, 200),
    }
  );
}

/**
 * Log a dictionary loading error
 *
 * @param dictionaryType - The type of dictionary (e.g., 'twl06', 'scowl60')
 * @param version - The version number of the dictionary
 * @param errorMessage - The error message
 * @param retryCount - The number of retries attempted
 */
export function logDictionaryLoadError(
  dictionaryType: string,
  version: number,
  errorMessage: string,
  retryCount: number
): void {
  logEvent(
    EventTypes.ERROR_DICTIONARY_LOAD,
    EventCategories.ERROR,
    {
      dictionary_type: dictionaryType,
      dictionary_version: version,
      error_message: errorMessage,
      retry_count: retryCount,
      page_url: window.location.pathname,
      user_agent: navigator.userAgent.substring(0, 200),
    }
  );
}

/**
 * Initialize global error handlers to capture unhandled errors and rejections.
 * Call this once at app startup.
 */
export function initializeErrorTracking(): void {
  // Handle uncaught JavaScript errors
  window.onerror = (message, source, lineno, colno, error) => {
    logJavaScriptError(
      String(message),
      source,
      lineno,
      colno,
      error
    );
    // Return false to allow the error to propagate to the console
    return false;
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    logUnhandledRejection(event.reason);
  };

  // Handle resource loading errors (images, scripts, etc.)
  window.addEventListener('error', (event) => {
    const target = event.target as HTMLElement;

    // Only handle resource loading errors, not JavaScript errors
    if (target && target !== window && 'src' in target) {
      const src = (target as HTMLImageElement | HTMLScriptElement).src;
      const tagName = target.tagName?.toLowerCase() || 'unknown';

      logAssetLoadError(tagName, src, 'Resource failed to load');
    }
  }, true); // Use capture phase to catch errors before they bubble

  if (isDevelopmentMode()) {
    console.log('📊 Error tracking initialized');
  }
}

/**
 * Route Source Enum
 * 
 * This enum defines all the sources from which navigation can be triggered.
 * This helps track user behavior and understand which UI elements are most used.
 */
export const RouteSource = {
  HEADER: 'header',
  SIDEBAR: 'sidebar',
  GAME_STATS_MODAL: 'game_stats_modal',
} as const;

// Type for route sources
export type RouteSourceType = typeof RouteSource[keyof typeof RouteSource];

// System events
/**
 * Log when a dictionary is successfully loaded
 * 
 * @param dictionaryType - The type of dictionary (e.g., 'twl06', 'scowl60')
 * @param version - The version number of the dictionary
 * @param patch - The patch number of the dictionary
 * @param wordCount - The total number of words in the loaded dictionary
 * @param wasCached - Whether the dictionary was loaded from cache
 */
export function logDictionaryLoaded(
  dictionaryType: string,
  version: number,
  patch: number,
  wordCount: number,
  wasCached: boolean
): void {
  logEvent(
    EventTypes.DICTIONARY_LOADED,
    EventCategories.SYSTEM,
    {
      dictionary_type: dictionaryType,
      dictionary_version: version,
      dictionary_patch: patch,
      word_count: wordCount,
      was_cached: wasCached
    }
  );
}